import React, { useCallback, useEffect, useState } from 'react';
import {
    Box,
    CircularProgress,
    InputAdornment,
    MenuItem,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    TextField,
    Tooltip,
    Typography,
    makeStyles,
} from '@material-ui/core';
import SearchIcon from '@material-ui/icons/Search';
import { getStaffClientes } from '../../services/staff-lector';

const useStyles = makeStyles(theme => ({
    toolbar: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: theme.spacing(2),
        marginBottom: theme.spacing(2.5),
        flexWrap: 'wrap',
    },
    filterField: {
        minWidth: 280,
        '& .MuiOutlinedInput-root': {
            borderRadius: 5,
            backgroundColor: theme.palette.surface.popover,
        },
        '& .MuiOutlinedInput-input': {
            paddingTop: theme.spacing(1.1),
            paddingBottom: theme.spacing(1.1),
        },
    },
    sortSelect: {
        minWidth: 180,
        '& .MuiOutlinedInput-root': {
            borderRadius: 5,
            backgroundColor: theme.palette.surface.popover,
        },
        '& .MuiOutlinedInput-input': {
            paddingTop: theme.spacing(1.1),
            paddingBottom: theme.spacing(1.1),
        },
    },
    countLabel: {
        fontSize: 13,
        color: theme.palette.text.disabled,
    },
    listContainer: {
        borderRadius: 5,
        border: `1px solid ${theme.palette.border.soft}`,
        boxShadow: theme.palette.shadow.card,
        overflow: 'hidden',
        backgroundColor: theme.palette.common.white,
    },
    tableHead: {
        backgroundColor: theme.palette.surface.panel,
    },
    headCell: {
        color: theme.palette.text.section,
        fontWeight: 700,
        borderBottom: `2px solid ${theme.palette.border.main}`,
        whiteSpace: 'nowrap',
        fontSize: 12,
        textTransform: 'uppercase',
        letterSpacing: 0.5,
    },
    bodyRow: {
        '&:nth-child(even)': {
            backgroundColor: theme.palette.surface.soft,
        },
    },
    bodyCell: {
        borderBottom: `1px solid ${theme.palette.border.subtle}`,
        color: theme.palette.text.secondary,
        whiteSpace: 'nowrap',
    },
    emailCell: {
        borderBottom: `1px solid ${theme.palette.border.subtle}`,
        fontWeight: 700,
        color: theme.palette.grey.main,
        whiteSpace: 'nowrap',
        maxWidth: 0,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
    },
    nameCell: {
        borderBottom: `1px solid ${theme.palette.border.subtle}`,
        color: theme.palette.text.secondary,
        whiteSpace: 'nowrap',
        maxWidth: 0,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
    },
    rateHigh: {
        display: 'inline-block',
        padding: '2px 8px',
        borderRadius: 4,
        fontSize: 11,
        fontWeight: 600,
        backgroundColor: theme.palette.status.successSoft,
        color: theme.palette.status.success,
    },
    rateMid: {
        display: 'inline-block',
        padding: '2px 8px',
        borderRadius: 4,
        fontSize: 11,
        fontWeight: 600,
        backgroundColor: theme.palette.warning.light,
        color: theme.palette.warning.dark,
    },
    rateLow: {
        display: 'inline-block',
        padding: '2px 8px',
        borderRadius: 4,
        fontSize: 11,
        fontWeight: 600,
        backgroundColor: theme.palette.error.light,
        color: theme.palette.error.dark,
    },
    centeredState: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: 180,
    },
    pagination: {
        borderTop: `1px solid ${theme.palette.border.subtle}`,
    },
    sortLabel: {
        width: '100%',
        justifyContent: 'flex-end',
    },
}));

const SORT_OPTIONS = [
    { value: 'facturas_total',  label: 'Total facturas' },
    { value: 'facturas_ok',     label: 'Facturas OK' },
    { value: 'tasa_exito',      label: 'Tasa de éxito' },
    { value: 'score_promedio',  label: 'Score promedio' },
    { value: 'email',           label: 'Email' },
    { value: 'razon_social',    label: 'Razón social' },
];

// px widths kept here so colgroup and head cells share the same source
const COLS = [
    { id: 'email',          label: 'Email',        align: 'left',  sortKey: 'email',          width: 200 },
    { id: 'razon_social',   label: 'Razón social', align: 'left',  sortKey: 'razon_social',   width: 160 },
    { id: 'facturas_total', label: 'Total',        align: 'right', sortKey: 'facturas_total',  width: 74 },
    { id: 'facturas_ok',    label: 'OK',           align: 'right', sortKey: 'facturas_ok',     width: 74 },
    { id: 'tasa_exito',     label: 'Tasa éxito',   align: 'right', sortKey: 'tasa_exito',      width: 100 },
    { id: 'score_promedio', label: 'Score prom.',  align: 'right', sortKey: 'score_promedio',  width: 100 },
    { id: 'facturas_error', label: 'Error',        align: 'right', sortKey: null,              width: 74 },
    { id: 'huerfanas',      label: 'Huérfanas',    align: 'right', sortKey: null,              width: 88 },
    { id: 'hoy',            label: 'Hoy',          align: 'right', sortKey: null,              width: 68 },
    { id: 'semana',         label: 'Semana',       align: 'right', sortKey: null,              width: 80 },
];

function RateChip({ pct }) {
    const classes = useStyles();
    if (pct == null) return <span>—</span>;
    if (pct >= 90) return <span className={classes.rateHigh}>{pct.toFixed(1)}%</span>;
    if (pct >= 70) return <span className={classes.rateMid}>{pct.toFixed(1)}%</span>;
    return <span className={classes.rateLow}>{pct.toFixed(1)}%</span>;
}

function fmt(n, decimals = 0) {
    if (n == null) return '—';
    return Number(n).toLocaleString('es-AR', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
}

function cellValue(col, c, classes) {
    switch (col.id) {
        case 'email':
            return (
                <TableCell key={col.id} className={classes.emailCell}>
                    <Tooltip title={c.email} arrow>
                        <span>{c.email || '—'}</span>
                    </Tooltip>
                </TableCell>
            );
        case 'razon_social':
            return (
                <TableCell key={col.id} className={classes.nameCell}>
                    <Tooltip title={c.razon_social || c.nombre || ''} arrow>
                        <span>{c.razon_social || c.nombre || '—'}</span>
                    </Tooltip>
                </TableCell>
            );
        case 'facturas_total':   return <TableCell key={col.id} className={classes.bodyCell} align="right">{fmt(c.facturas_total)}</TableCell>;
        case 'facturas_ok':      return <TableCell key={col.id} className={classes.bodyCell} align="right">{fmt(c.facturas_ok)}</TableCell>;
        case 'tasa_exito':       return <TableCell key={col.id} className={classes.bodyCell} align="right"><RateChip pct={c.tasa_exito} /></TableCell>;
        case 'score_promedio':   return <TableCell key={col.id} className={classes.bodyCell} align="right">{c.score_promedio != null ? fmt(c.score_promedio, 1) : '—'}</TableCell>;
        case 'facturas_error':   return <TableCell key={col.id} className={classes.bodyCell} align="right">{fmt(c.facturas_error)}</TableCell>;
        case 'huerfanas':        return <TableCell key={col.id} className={classes.bodyCell} align="right">{fmt(c.huerfanas)}</TableCell>;
        case 'hoy':              return <TableCell key={col.id} className={classes.bodyCell} align="right">{fmt(c.actividad?.hoy)}</TableCell>;
        case 'semana':           return <TableCell key={col.id} className={classes.bodyCell} align="right">{fmt(c.actividad?.semana)}</TableCell>;
        default:                 return <TableCell key={col.id} className={classes.bodyCell} />;
    }
}

export default function ClientesTab() {
    const classes = useStyles();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [searchInput, setSearchInput] = useState('');
    const [search, setSearch] = useState('');
    const [page, setPage] = useState(0);
    const [pageSize] = useState(30);
    const [orderBy, setOrderBy] = useState('facturas_total');
    const [orderDir, setOrderDir] = useState('desc');

    const fetchData = useCallback(() => {
        setLoading(true);
        getStaffClientes({ search, ordenar_por: orderBy, ordenar_dir: orderDir, page: page + 1, page_size: pageSize })
            .then(res => setData(res.data))
            .catch(() => setData(null))
            .finally(() => setLoading(false));
    }, [search, orderBy, orderDir, page, pageSize]);

    useEffect(() => { fetchData(); }, [fetchData]);


    const handleSearch = e => {
        e.preventDefault();
        setSearch(searchInput);
        setPage(0);
    };

    return (
        <Box>
            <Box className={classes.toolbar}>
                <Box display="flex" alignItems="center" style={{ gap: 12, flexWrap: 'wrap' }}>
                    <form onSubmit={handleSearch}>
                        <TextField
                            className={classes.filterField}
                            variant="outlined"
                            size="small"
                            placeholder="Buscar por email, nombre o razón social..."
                            value={searchInput}
                            onChange={e => setSearchInput(e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon fontSize="small" color="disabled" />
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </form>
                    <TextField
                        select
                        className={classes.sortSelect}
                        variant="outlined"
                        size="small"
                        label="Ordenar por"
                        value={orderBy}
                        onChange={e => { setOrderBy(e.target.value); setPage(0); }}
                    >
                        {SORT_OPTIONS.map(opt => (
                            <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                        ))}
                    </TextField>
                    <TextField
                        select
                        className={classes.sortSelect}
                        variant="outlined"
                        size="small"
                        label="Órden"
                        value={orderDir}
                        onChange={e => { setOrderDir(e.target.value); setPage(0); }}
                        style={{ minWidth: 120 }}
                    >
                        <MenuItem value="desc">Descendente</MenuItem>
                        <MenuItem value="asc">Ascendente</MenuItem>
                    </TextField>
                </Box>
                {data && (
                    <Typography className={classes.countLabel}>
                        {fmt(data.count)} cliente{data.count !== 1 ? 's' : ''}
                    </Typography>
                )}
            </Box>

            <TableContainer className={classes.listContainer}>
                <Table size="small" style={{ tableLayout: 'fixed', width: '100%' }}>
                    <colgroup>
                        {COLS.map(col => (
                            <col key={col.id} style={{ width: col.width }} />
                        ))}
                    </colgroup>
                    <TableHead className={classes.tableHead}>
                        <TableRow>
                            {COLS.map(col => (
                                <TableCell
                                    key={col.id}
                                    className={classes.headCell}
                                    align={col.align}
                                >
                                    {col.label}
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading ? (
                            <TableRow>
                                <TableCell colSpan={COLS.length}>
                                    <Box className={classes.centeredState}>
                                        <CircularProgress size={28} />
                                    </Box>
                                </TableCell>
                            </TableRow>
                        ) : (data?.results || []).map(c => (
                            <TableRow key={c.cliente_id} className={classes.bodyRow}>
                                {COLS.map(col => cellValue(col, c, classes))}
                            </TableRow>
                        ))}
                        {!loading && data?.results?.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={COLS.length} align="center" style={{ padding: 24 }}>
                                    <Typography variant="body2" color="textSecondary">Sin resultados</Typography>
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
                {data && (
                    <TablePagination
                        className={classes.pagination}
                        component="div"
                        count={data.count}
                        page={page}
                        rowsPerPage={pageSize}
                        rowsPerPageOptions={[30]}
                        onPageChange={(_, p) => setPage(p)}
                        labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${count}`}
                    />
                )}
            </TableContainer>
        </Box>
    );
}
